/* Detects the Help Scout Beacon chat launcher (injected externally via
   GTM/app embed as #beacon-container, directly under <body>) and publishes
   its real measured footprint as CSS custom properties, so fixed bottom
   UI (e.g. the product sticky bar) can reserve exactly the space it needs
   instead of a hardcoded guess.

   Not dependent on the `Beacon()` JS global - its availability/timing
   varies by Beacon version/loader - only on the DOM node itself. */
(() => {
  if (window.__beaconClearanceBound) return;
  window.__beaconClearanceBound = true;

  const FALLBACK_INLINE = '4.5rem';
  const FALLBACK_BLOCK = '0px';
  const GRACE_TIMEOUT_MS = 4000;
  const root = document.documentElement;

  let resizeObserver = null;
  let currentEl = null;
  let graceTimer = window.setTimeout(() => {
    if (!currentEl) publish('1rem', FALLBACK_BLOCK);
  }, GRACE_TIMEOUT_MS);

  function publish(inline, block) {
    root.style.setProperty('--beacon-clearance-inline', inline);
    root.style.setProperty('--beacon-clearance-block', block);
  }

  // Safe default until detection resolves - avoids a layout flash/jump the
  // moment fixed bottom UI first paints, before Beacon (if any) has loaded.
  publish(FALLBACK_INLINE, FALLBACK_BLOCK);

  function isVisibleBottomLeft(rect) {
    if (rect.width === 0 || rect.height === 0) return false;
    const nearLeft = rect.left < window.innerWidth * 0.5;
    const nearBottom = rect.bottom > window.innerHeight * 0.5;
    return nearLeft && nearBottom;
  }

  function measure(el) {
    const rect = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none' || !isVisibleBottomLeft(rect)) {
      publish('1rem', FALLBACK_BLOCK);
      return;
    }
    publish(`calc(${Math.ceil(rect.right)}px + 0.75rem)`, `calc(${Math.ceil(rect.height)}px + 0.5rem)`);
  }

  function watch(el) {
    window.clearTimeout(graceTimer);
    currentEl = el;
    measure(el);
    resizeObserver?.disconnect();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => measure(el));
      resizeObserver.observe(el);
    }
  }

  function unwatch() {
    resizeObserver?.disconnect();
    resizeObserver = null;
    currentEl = null;
    publish('1rem', FALLBACK_BLOCK);
  }

  const bodyObserver = new MutationObserver(() => {
    const el = document.getElementById('beacon-container');
    if (el && el !== currentEl) watch(el);
    else if (!el && currentEl) unwatch();
  });
  bodyObserver.observe(document.body, { childList: true });

  const existing = document.getElementById('beacon-container');
  if (existing) watch(existing);

  window.addEventListener('resize', () => currentEl && measure(currentEl));
  window.addEventListener('orientationchange', () => currentEl && measure(currentEl));
})();
